const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const isValidDate = (date: Date): boolean => !Number.isNaN(date.getTime());

/**
 * Formats an ISO 8601 date string as a short locale date (e.g. "Mar 20, 2026").
 */
export function formatShortDate(iso: string): string {
  return SHORT_DATE_FORMATTER.format(new Date(iso));
}

/**
 * Returns the age of an ISO 8601 date as a short relative string
 * (e.g. "69d", "1d", or "today").
 */
export function formatAgeDays(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1d';
  return `${days}d`;
}

/**
 * Formats a date+time from an ISO 8601 string (e.g. "Mar 20, 2026 3:45 PM").
 */
export function getFullTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    if (!isValidDate(date)) return '-';
    const datePart = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  } catch {
    return '-';
  }
}

/**
 * Formats a duration in milliseconds as a precise human-readable string.
 */
export function formatDurationPrecise(durationMs: number): string {
  if (durationMs < 1000) return `${durationMs}ms`;
  const seconds = durationMs / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Formats a duration in milliseconds as a compact string for tables.
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const remainS = Math.floor(s % 60);
  return `${m}m ${remainS}s`;
}

/**
 * Returns a relative "time ago" string from an ISO 8601 timestamp.
 */
export function getTimeAgo(timestamp: string | null | undefined): string {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    if (!isValidDate(date)) return '';
    const now = Date.now();
    const diffMs = now - date.getTime();
    if (diffMs < 0) return '';
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch {
    return '';
  }
}

/**
 * Formats an ISO 8601 timestamp as a readable date + time with seconds
 * (e.g. "Mar 30, 2026 3:18:42 PM").
 */
export function formatDateTimeSeconds(timestamp: string | null | undefined): string {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp);
    if (!isValidDate(date)) return '-';
    const datePart = date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
    return `${datePart} ${timePart}`;
  } catch {
    return '-';
  }
}

/**
 * Format an epoch-millisecond timestamp as a compact "time ago" string
 * (``"just now"``, ``"7m ago"``, ``"3h ago"``, ``"5d ago"``) and fall
 * back to a short absolute date once it gets older than a month.
 *
 * Returns ``null`` when the input is missing or non-finite — callers
 * can then collapse the metadata row instead of rendering a stale or
 * malformed value.
 */
export function formatRelativeFromEpochMs(epochMs: number | null | undefined): string | null {
  if (epochMs == null || !Number.isFinite(epochMs)) return null;
  const diffMs = Date.now() - epochMs;
  const seconds = Math.max(0, Math.floor(diffMs / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(epochMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Formats an ISO timestamp as a relative or short absolute string.
 */
export function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
